import type {
  CapacitySearchInput,
  CapacitySearchResult,
  LoadBoardAdapter,
  LoadBoardCarrierResponse,
  LoadBoardCarrierResponseInput,
  LoadBoardDraft,
  LoadBoardPosting,
  LoadBoardPostingInput,
  LoadBoardPostingUpdateInput,
  LoadBoardProvider,
} from "./types";
import { LoadBoardProviderError } from "./types";

export function createMockLoadBoardAdapter(provider: LoadBoardProvider): LoadBoardAdapter {
  const postings = new Map<string, LoadBoardPosting>();

  return {
    provider,
    async draftPostLoad(input: LoadBoardPostingInput): Promise<LoadBoardDraft> {
      return {
        provider,
        loadId: input.loadId,
        providerTraceId: `${provider}_mock_draft_${input.loadId}`,
        status: "draft",
        createdAt: new Date().toISOString(),
        normalizedFields: input,
        validationWarnings: draftWarnings(input),
      };
    },
    async postLoad(input: LoadBoardPostingInput) {
      const now = new Date().toISOString();
      const providerPostingId = `${provider}_${input.loadId}_${postings.size + 1}`;
      const posting: LoadBoardPosting = {
        provider,
        providerPostingId,
        loadId: input.loadId,
        providerTraceId: `${provider}_mock_post_${providerPostingId}`,
        status: "posted",
        postedAt: now,
        expiresAt: new Date(Date.parse(now) + 6 * 3_600_000).toISOString(),
        normalizedFields: input,
      };
      postings.set(providerPostingId, posting);
      return posting;
    },
    async updatePosting(input: LoadBoardPostingUpdateInput) {
      const posting = postings.get(input.providerPostingId);
      if (!posting) {
        throw new LoadBoardProviderError(
          provider,
          "posting_not_found",
          `posting not found: ${input.providerPostingId}`,
          false,
        );
      }

      const updated: LoadBoardPosting = {
        ...posting,
        status: "updated",
        expiresAt: input.expiresAt ?? posting.expiresAt,
        normalizedFields: {
          ...posting.normalizedFields,
          pickupWindowStart: input.pickupWindowStart ?? posting.normalizedFields.pickupWindowStart,
          pickupWindowEnd: input.pickupWindowEnd ?? posting.normalizedFields.pickupWindowEnd,
          targetRateCents: input.targetRateCents ?? posting.normalizedFields.targetRateCents,
        },
      };
      postings.set(input.providerPostingId, updated);
      return updated;
    },
    async searchCapacity(input: CapacitySearchInput): Promise<CapacitySearchResult> {
      return {
        provider,
        loadId: input.loadId,
        providerTraceId: `${provider}_mock_search_${input.loadId}`,
        searchedAt: new Date().toISOString(),
        capacity: [
          {
            provider,
            carrierName: provider === "dat" ? "DAT Prime Logistics" : "Truckstop Prime Logistics",
            carrierMc: "100100",
            contactEmail: "dispatch@prime.example",
            contactPhone: "+13125550101",
            originDeadheadMiles: 18,
            targetRateCents: 255000,
            fitScore: 94,
            evidenceSummary: "Strong lane match, close origin deadhead, recent activity.",
          },
          {
            provider,
            carrierName: "Blue Ridge Freight",
            carrierMc: "200200",
            contactEmail: "ops@blueridge.example",
            contactPhone: "+13125550102",
            originDeadheadMiles: 42,
            targetRateCents: 270000,
            fitScore: 83,
            evidenceSummary: "Good equipment match with moderate deadhead.",
          },
          {
            provider,
            carrierName: "Crosswind Transport",
            carrierMc: "300300",
            contactEmail: "loads@crosswind.example",
            contactPhone: "+13125550103",
            originDeadheadMiles: 96,
            targetRateCents: 295000,
            fitScore: 68,
            evidenceSummary: "Available but weaker lane fit and higher target rate.",
          },
        ].slice(0, input.maxResults ?? 3),
      };
    },
    rankCapacity(capacity) {
      return [...capacity].sort((left, right) => {
        if (right.fitScore !== left.fitScore) return right.fitScore - left.fitScore;
        if (left.targetRateCents !== right.targetRateCents) {
          return left.targetRateCents - right.targetRateCents;
        }
        return left.originDeadheadMiles - right.originDeadheadMiles;
      });
    },
    async recordCarrierResponse(
      input: LoadBoardCarrierResponseInput,
    ): Promise<LoadBoardCarrierResponse> {
      return {
        ...input,
        provider,
        providerTraceId: `${provider}_mock_response_${input.loadId}`,
        receivedAt: input.receivedAt ?? new Date().toISOString(),
      };
    },
    async expirePosting(providerPostingId: string) {
      const posting = postings.get(providerPostingId);
      if (!posting) {
        throw new LoadBoardProviderError(
          provider,
          "posting_not_found",
          `posting not found: ${providerPostingId}`,
          false,
        );
      }
      const expired = { ...posting, status: "expired" as const };
      postings.set(providerPostingId, expired);
      return expired;
    },
  };
}

function draftWarnings(input: LoadBoardPostingInput): string[] {
  const warnings: string[] = [];
  if (!input.targetRateCents) warnings.push("target rate is not set");
  if (!input.weightLbs) warnings.push("weight is not set");
  return warnings;
}
